import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { VehicleDTO } from '@wf/shared'
import { STATUS_LABEL } from '../orders/labels'
import { formatDateDE } from '../time/format'
import { useAnonymizeVehicle, useDeleteVehicle, useVehicleHistory } from './useCustomers'

/**
 * Eine Fahrzeugkarte am Kundendetail: Anzeige (Kennzeichen/Modell/FIN) plus die zuvor toten Endpunkte —
 * Auftragshistorie aufklappen (lädt erst dann), anonymisieren (DSGVO ohne Datenverlust) und löschen.
 * Bearbeiten bleibt beim Parent (er zeigt das VehicleEditForm an); hier nur der Anstoß über onEdit.
 */
export function VehicleCard({ vehicle, customerId, onEdit }: { vehicle: VehicleDTO; customerId: number; onEdit: () => void }) {
  const remove = useDeleteVehicle(customerId)
  const anonymize = useAnonymizeVehicle(customerId)
  const [showHistory, setShowHistory] = useState(false)
  const history = useVehicleHistory(vehicle.id, showHistory)
  const label = vehicle.plate || 'ohne Kennzeichen'
  const title = [vehicle.make, vehicle.model].filter(Boolean).join(' ') || 'Fahrzeug'
  // Motor-Kurzfakten (nur was vorhanden ist), z. B. „1968 cm³ · 110 kW · Diesel".
  const engine = [
    vehicle.displacementCcm ? `${vehicle.displacementCcm} cm³` : null,
    vehicle.powerKw ? `${vehicle.powerKw} kW` : null,
    vehicle.fuelType,
  ]
    .filter(Boolean)
    .join(' · ')

  function runDelete(): void {
    if (window.confirm(`Fahrzeug ${label} löschen?`)) remove.mutate(vehicle.id)
  }
  function runAnonymize(): void {
    if (window.confirm(`Fahrzeug ${label} anonymisieren? Die Aufträge bleiben erhalten.`)) anonymize.mutate(vehicle.id)
  }

  return (
    <div className="wf-card wf-card--static">
      <div className="wf-card__row">
        <span className="wf-plate">
          <span className="wf-plate__eu">D</span>
          <span className="wf-plate__nr">{vehicle.plate || '—'}</span>
        </span>
        <span className="wf-card__title">{title}</span>
        <button type="button" className="wf-iconbtn wf-card__edit" aria-label={`Fahrzeug bearbeiten: ${label}`} onClick={onEdit}>
          ✎
        </button>
      </div>
      {/* Fahrzeugschein-Stammdaten — jeweils nur zeigen, was gepflegt ist. HSN/TSN = Teilekatalog-Schlüssel. */}
      {vehicle.hsn || vehicle.tsn ? (
        <div className="wf-card__meta">
          HSN/TSN: {vehicle.hsn ?? '—'} / {vehicle.tsn ?? '—'}
        </div>
      ) : null}
      {vehicle.vin ? <div className="wf-card__meta">FIN: {vehicle.vin}</div> : null}
      {vehicle.firstRegistration ? <div className="wf-card__meta">Erstzulassung: {formatDateDE(vehicle.firstRegistration)}</div> : null}
      {engine ? <div className="wf-card__meta">{engine}</div> : null}

      <div className="wf-vehicleactions">
        <button type="button" className="wf-btn wf-btn--ghost" aria-expanded={showHistory} onClick={() => setShowHistory((open) => !open)}>
          {showHistory ? 'Historie verbergen' : 'Historie'}
        </button>
        <button type="button" className="wf-btn wf-btn--ghost" disabled={anonymize.isPending} onClick={runAnonymize}>
          Fahrzeug anonymisieren
        </button>
        <button type="button" className="wf-btn wf-btn--danger" disabled={remove.isPending} onClick={runDelete}>
          Fahrzeug löschen
        </button>
      </div>

      {showHistory ? (
        history.isLoading ? (
          <div className="muted">Historie wird geladen…</div>
        ) : history.data && history.data.orders.length > 0 ? (
          <div className="wf-vehiclehistory">
            {history.data.orders.map((order) => (
              <Link key={order.id} to={`/orders/${order.id}`} className="wf-vehiclehistory__row">
                <span className="wf-card__nr">{order.nr ?? order.id}</span>
                <span className={`wf-pill wf-pill--${order.status}`}>{STATUS_LABEL[order.status] ?? order.status}</span>
                <span className="wf-card__meta">{formatDateDE(order.createdAt)}</span>
              </Link>
            ))}
          </div>
        ) : (
          <div className="wf-card__meta">Keine Aufträge für dieses Fahrzeug.</div>
        )
      ) : null}
    </div>
  )
}
